import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { LoadingState } from '@/components/common/LoadingSpinner';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useSessionMonitor } from '@/hooks/useSessionMonitor';
import { SessionExpirationModal } from '@/components/common/SessionExpirationModal';
import AdminLayout from '@/components/common/AdminLayout';
import { initializePrefetch } from '@/utils/prefetch';
import './App.css';

// Lazy load pages for better performance
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const Users = React.lazy(() => import('@/pages/Users'));
const AdminUsers = React.lazy(() => import('@/pages/AdminUsers'));
const Analytics = React.lazy(() => import('@/pages/Analytics'));
const Payments = React.lazy(() => import('@/pages/Payments'));
const Quotas = React.lazy(() => import('@/pages/Quotas'));
const Referrals = React.lazy(() => import('@/pages/Referrals'));
const Announcements = React.lazy(() => import('@/pages/Announcements'));
const AuditLogs = React.lazy(() => import('@/pages/AuditLogs'));
const Teams = React.lazy(() => import('@/pages/Teams'));
const TeamDetails = React.lazy(() => import('@/pages/TeamDetails'));
const EmailNotifications = React.lazy(() => import('@/pages/EmailNotifications'));
const EmailsLayout = React.lazy(() => import('@/pages/EmailsLayout'));
const EmailsCampaigns = React.lazy(() => import('@/pages/EmailsCampaigns'));
const ForumModeration = React.lazy(() => import('@/pages/ForumModeration'));

const Login = React.lazy(() => import('@/pages/Login'));
const DebugAuth = React.lazy(() => import('@/pages/DebugAuth'));
const TestAuth = React.lazy(() => import('@/pages/TestAuth'));

// Protected route wrapper
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAdminAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingState message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  return <AdminLayout>{children}</AdminLayout>;
};

// Public route wrapper (for login page)
const PublicRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAdminAuth();

  if (isLoading) {
    return <LoadingState message="Checking authentication..." />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  // Session monitoring for all authenticated users
  const sessionMonitor = useSessionMonitor();
  const queryClient = useQueryClient();

  // Initialize prefetching for critical dashboard data
  useEffect(() => {
    initializePrefetch(queryClient);
  }, [queryClient]);

  return (
    <ErrorBoundary>
      {/* Session expiration modal - global across the entire app */}
      <SessionExpirationModal
        isOpen={sessionMonitor.isModalOpen}
        sessionState={sessionMonitor.sessionState}
        timeRemaining={sessionMonitor.timeRemaining}
        onExtendSession={sessionMonitor.extendSession}
        onSignOut={sessionMonitor.signOut}
        onClose={sessionMonitor.closeModal}
        isExtending={sessionMonitor.isExtending}
      />

      <Router>
        <div className="min-h-screen bg-gray-50">
          <Suspense fallback={<LoadingState message="Loading application..." />}>
            <Routes>
              {/* Public routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />

              {/* Debug route */}
              <Route
                path="/debug"
                element={
                  <DebugAuth />
                }
              />

              {/* Test route */}
              <Route
                path="/test"
                element={
                  <TestAuth />
                }
              />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <Users />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin-users"
                element={
                  <ProtectedRoute>
                    <AdminUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments"
                element={
                  <ProtectedRoute>
                    <Payments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quotas"
                element={
                  <ProtectedRoute>
                    <Quotas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/referrals"
                element={
                  <ProtectedRoute>
                    <Referrals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teams"
                element={
                  <ProtectedRoute>
                    <Teams />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teams/:orgId"
                element={
                  <ProtectedRoute>
                    <TeamDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/announcements"
                element={
                  <ProtectedRoute>
                    <Announcements />
                  </ProtectedRoute>
                }
              />
              {/* Emails routes with sub-navigation */}
              <Route
                path="/emails"
                element={
                  <ProtectedRoute>
                    <EmailsLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="send-now"
                  element={<EmailNotifications embedded={true} />}
                />
                <Route
                  path="campaigns"
                  element={<EmailsCampaigns />}
                />
              </Route>
              {/* Redirect old email-notifications route */}
              <Route
                path="/email-notifications"
                element={<Navigate to="/emails" replace />}
              />
              <Route
                path="/audit-logs"
                element={
                  <ProtectedRoute>
                    <AuditLogs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/forum"
                element={
                  <ProtectedRoute>
                    <ForumModeration />
                  </ProtectedRoute>
                }
              />


              {/* Catch all route - redirect to dashboard */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;



